/**
 * Org settings: which groups may invite people (docs/Decisions.md D51 item 2).
 * Org admins and moderators can always invite; tick groups to let their
 * members invite too. Saved as `settings.inviteGroupIds` through
 * PUT /organizations/{orgId}, which checks every group belongs to the org.
 */
import { useEffect, useState } from 'react';
import * as orgApi from '../api/organizations';
import * as groupsApi from '../api/groups';
import { useOrg } from '../lib/org';
import { useToast } from './toast';
import { ErrorAlert, Spinner, EmptyState } from './ui';
import { cn, ui } from '../lib/ui';

export default function InviteGroupsSetting() {
  const { activeOrg, refresh } = useOrg();
  const toast = useToast();
  const [groups, setGroups] = useState(null);
  const [selected, setSelected] = useState(() => new Set(activeOrg?.inviteGroupIds || []));
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelected(new Set(activeOrg?.inviteGroupIds || []));
  }, [activeOrg?.orgId, activeOrg?.inviteGroupIds]);

  useEffect(() => {
    groupsApi
      .listGroups({ limit: 100 })
      .then(d => setGroups((d.groups || []).filter(g => !g.orgId || g.orgId === activeOrg?.orgId)))
      .catch(err => {
        setGroups([]);
        setError(err);
      });
  }, [activeOrg?.orgId]);

  function toggle(groupId, on) {
    setSelected(current => {
      const next = new Set(current);
      if (on) next.add(groupId);
      else next.delete(groupId);
      return next;
    });
  }

  async function onSave(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await orgApi.updateOrganization(activeOrg.orgId, {
        settings: { inviteGroupIds: [...selected] },
      });
      toast.success('Invitation settings saved.');
      if (refresh) await refresh();
    } catch (err) {
      setError(err);
      toast.error(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="mt-8" aria-labelledby="invite-groups-title">
      <h2 id="invite-groups-title" className={ui.h2}>
        Who can invite people
      </h2>
      <form onSubmit={onSave} className={ui.card}>
        <ErrorAlert error={error} />
        <p className={cn(ui.muted, 'text-body-s')}>
          Org admins and moderators can always invite. Tick the groups whose members may invite
          people too; they invite as members only.
        </p>
        {groups === null ? <Spinner /> : null}
        {groups && groups.length === 0 ? (
          <EmptyState message="This organization has no groups yet." />
        ) : null}
        {groups && groups.length > 0 ? (
          <fieldset className="mt-2">
            <legend className={ui.label}>Groups that can invite</legend>
            {groups.map(group => (
              <label key={group.groupId} className={cn(ui.row, 'text-body-s')}>
                <input
                  type="checkbox"
                  checked={selected.has(group.groupId)}
                  onChange={e => toggle(group.groupId, e.target.checked)}
                />{' '}
                {group.name}
              </label>
            ))}
          </fieldset>
        ) : null}
        <button type="submit" className={cn(ui.btnPrimary, 'mt-4')} disabled={saving || !groups}>
          {saving ? 'Saving…' : 'Save invitation settings'}
        </button>
      </form>
    </section>
  );
}
